import { Link } from "react-router-dom";
import { ArrowUpRight, Quote } from "lucide-react";
import Reveal from "@/components/Reveal";
import { COLLECTION, HERO_IMAGES, OWNER, ABOUT_STORY } from "@/data/site";

const FEATURED_SLUGS = ["1951-jowett-jupiter", "1933-wolseley-hornet", "1953-sunbeam-talbot", "1960-triumph-herald"];

export default function Home() {
  const featured = COLLECTION.filter((c) => FEATURED_SLUGS.includes(c.slug));

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-noise">
        <div className="absolute inset-0">
          <img
            src={HERO_IMAGES.primary}
            alt="The 1951 Jowett Jupiter"
            className="w-full h-full object-cover object-[60%_center]"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[color:var(--bg)] via-[color:var(--bg)]/50 to-transparent"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[color:var(--bg)]"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-28 pb-40 md:pt-40 md:pb-56">
          <Reveal>
            <div className="eyebrow mb-6">Est. 1980s · A private British collection</div>
          </Reveal>
          <Reveal delay={120}>
            <h1 className="font-display letterpress text-[3.2rem] leading-[0.95] md:text-[6.5rem] lg:text-[8rem] tracking-tighter max-w-5xl">
              Caretaker of <span className="italic text-[color:var(--brg)]">British</span> motoring <span className="italic">heritage.</span>
            </h1>
          </Reveal>
          <Reveal delay={240}>
            <div className="mt-12 grid md:grid-cols-12 gap-8 items-end">
              <p className="md:col-span-7 text-lg md:text-xl text-[color:var(--ink-secondary)] leading-relaxed max-w-2xl">
                No power brakes. No power steering. No automatic transmission. These cars demand our attention — and reward it with a kind of motoring nothing modern can imitate.
              </p>
              <div className="md:col-span-5 flex md:justify-end gap-4 flex-wrap">
                <Link to="/collection" data-testid="hero-view-collection-btn" className="btn-primary">
                  View the Collection <ArrowUpRight size={16} />
                </Link>
                <Link to="/about" data-testid="hero-our-story-btn" className="btn-ghost">
                  Our Story
                </Link>
              </div>
            </div>
          </Reveal>
        </div>

        {/* Bottom marquee */}
        <div className="relative border-y border-[color:var(--accent)] bg-[color:var(--bg)]/85 py-5 overflow-hidden">
          <div className="marquee-track flex gap-16 whitespace-nowrap font-display text-2xl md:text-3xl italic text-[color:var(--ink-secondary)]">
            {Array.from({ length: 2 }).flatMap((_, i) =>
              [
                "Jowett Jupiter",
                "Wolseley Hornet",
                "Sunbeam‑Talbot",
                "Alvis TA 21",
                "Morris Minor",
                "Austin Seven",
                "Triumph Herald",
                "Singer Roadster",
                "Ford Anglia",
              ].map((m) => (
                <span key={`${i}-${m}`} className="flex items-center gap-16">
                  <span>{m}</span>
                  <span className="text-[color:var(--oxblood)]">✦</span>
                </span>
              ))
            )}
          </div>
        </div>
      </section>

      {/* STORY SNIPPET */}
      <section className="parchment relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 grid md:grid-cols-12 gap-12 items-start">
          <Reveal className="md:col-span-5">
            <div className="eyebrow mb-6">No. 01 — The Caretaker</div>
            <h2 className="font-display text-5xl md:text-6xl tracking-tight leading-[1.02]">
              I don&apos;t sell <span className="italic text-[color:var(--brg)]">cars.</span>
              <br />I keep them <span className="italic">moving.</span>
            </h2>
          </Reveal>
          <Reveal className="md:col-span-7 md:pl-8 md:border-l border-[color:var(--accent)]" delay={150}>
            <Quote className="text-[color:var(--brg)] mb-6" size={28} />
            <p className="text-lg md:text-xl text-[color:var(--ink-secondary)] leading-relaxed">
              {ABOUT_STORY.split("\n\n")[0]}
            </p>
            <p className="mt-6 text-base text-[color:var(--ink-muted)] leading-relaxed">
              Every time I step into the garage, I smile and feel grateful for my good health. I cherish the friendships I&apos;ve built — and the famous &ldquo;donut drives&rdquo; we share on Sunday mornings.
            </p>
            <div className="mt-10 flex items-center gap-4">
              <span className="hairline w-16"></span>
              <span className="font-display italic text-xl">— {OWNER.name}, caretaker</span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FEATURED CARS — asymmetric */}
      <section className="border-t border-[color:var(--accent)] bg-[color:var(--surface)]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <Reveal>
            <div className="flex items-end justify-between flex-wrap gap-6 mb-16">
              <div>
                <div className="eyebrow mb-4">No. 02 — Selected motor cars</div>
                <h2 className="font-display text-5xl md:text-6xl tracking-tight">Four from the garage.</h2>
              </div>
              <Link to="/collection" data-testid="featured-see-all-link" className="nav-link">
                See all eleven →
              </Link>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-12 gap-6 md:gap-8">
            {featured.map((c, idx) => {
              const spans = ["md:col-span-7", "md:col-span-5", "md:col-span-5", "md:col-span-7"];
              const heights = ["aspect-[16/11]", "aspect-[4/5]", "aspect-[4/5]", "aspect-[16/11]"];
              return (
                <Reveal key={c.slug} className={spans[idx]} delay={idx * 100}>
                  <Link to={`/collection/${c.slug}`} className="group block" data-testid={`featured-car-${c.slug}`}>
                    <div className={`tile ${heights[idx]}`}>
                      <img src={c.image} alt={`${c.year} ${c.make} ${c.model}`} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                    <div className="mt-5 flex items-baseline justify-between gap-4">
                      <div>
                        <div className="font-display text-2xl md:text-3xl tracking-tight group-hover:text-[color:var(--brg)] transition">
                          {c.make} <span className="italic">{c.model}</span>
                        </div>
                        <div className="text-sm text-[color:var(--ink-muted)] mt-1">{c.year} · {c.note}</div>
                      </div>
                      <span className="font-display italic text-[color:var(--brg)] text-2xl">№{String(idx + 1).padStart(2, "0")}</span>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* DRIVES CALLOUT */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMAGES.convoy} alt="Sunday drive convoy" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[color:var(--ink)]/55"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-28 md:py-40 text-[color:var(--bg)]">
          <Reveal>
            <div className="eyebrow mb-6" style={{ color: "#D5CFC1" }}>Sunday mornings</div>
            <h2 className="font-display text-5xl md:text-7xl tracking-tight max-w-4xl letterpress">
              We drive so they all get <span className="italic">exercise.</span>
            </h2>
            <p className="mt-8 max-w-2xl text-lg text-[color:var(--bg)]/85">
              A small, dedicated circle of drivers meets on weekends to take the cars out for donuts, coffee, and a bit of countryside. We are always looking for new drivers who know how to drive a manual transmission.
            </p>
            <div className="mt-10 flex gap-4 flex-wrap">
              <Link to="/drives" data-testid="drives-explore-btn" className="btn-primary">
                Explore the Drives <ArrowUpRight size={16} />
              </Link>
              <Link to="/contact" data-testid="drives-join-btn" className="btn-ghost" style={{ color: "var(--bg)", borderColor: "var(--bg)" }}>
                Join us
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* QUICK LINKS */}
      <section className="parchment">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 grid md:grid-cols-3 gap-6">
          {[
            { to: "/memories", title: "Memorable Moments", desc: "Awards, weddings, costumes and the odd rainstorm.", img: HERO_IMAGES.detail },
            { to: "/videos", title: "Films & Interviews", desc: "Walk‑arounds, drives, Geneva Concours and more.", img: HERO_IMAGES.dashboard },
            { to: "/about", title: "Previously Owned", desc: "Roadmasters, Hillmans, MGs — the road that led here.", img: HERO_IMAGES.interior },
          ].map((card, idx) => (
            <Reveal key={card.to} delay={idx * 100}>
              <Link to={card.to} data-testid={`quicklink-${card.to.slice(1)}`} className="block group">
                <div className="tile aspect-[5/4] mb-5">
                  <img src={card.img} alt={card.title} className="w-full h-full object-cover" />
                </div>
                <div className="font-display text-2xl md:text-3xl tracking-tight group-hover:text-[color:var(--brg)] transition">
                  {card.title} <ArrowUpRight className="inline" size={18} />
                </div>
                <div className="text-sm text-[color:var(--ink-muted)] mt-2">{card.desc}</div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}
